<script lang="ts" setup>
import { BaseIconWand } from "#components";
import {
  EvaluationCriteria,
  EvaluationRelevantInstance,
} from "@/types/EvaluationResults";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { storeToRefs } from "pinia";
import type { ComputedRef } from 'vue';
import { CriteriaInstance } from "~/types/Checklist";

const {
  checklistCriteria,
  criteriaInstance,
  criteria,
  paragraphPosition,
  relevantInstance,
  isTopLevel,
} = defineProps({
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteria: {
    type: Object as PropType<EvaluationCriteria>,
    required: true,
  },
  relevantInstance: {
    type: Object as PropType<EvaluationRelevantInstance>,
    required: false,
  },
  paragraphPosition: {
    type: Number,
    required: false,
  },
  isTopLevel: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emits = defineEmits(["reportProblem"]);

const documentId = inject("documentId") as any;
const inheritedReviewInstance = inject<ComputedRef<EvaluationRelevantInstance | null> | null>('metricReviewRootInstance', null);
const rootReviewInstance = computed(() => inheritedReviewInstance?.value ?? relevantInstance ?? null);
provide('metricReviewRootInstance', rootReviewInstance);
const {
  saveEvaluationResults,
  updateManualParagraphPoints,
  updateManualPoints,
  processCriteria,
} = useStepResultsStore(documentId);

const onChange = () => {
  if (paragraphPosition != undefined) {
    updateManualParagraphPoints(paragraphPosition, undefined);
  }

  criteria.isOverriden = true;

  updateManualPoints(undefined);
  processCriteria();
  saveEvaluationResults();
};

const documentChecklistStore = useDocumentChecklistStore(documentId);

const setPassedOverride = async (
  passed: boolean,
  updateMechanics: boolean = true
) => {
  criteria.passed = passed;
  criteria.isOverriden = true;

  // This will need to be removed if Mike can pass back a valid value
  if (criteria.instanceUid == undefined) {
    criteria.instanceUid = useGenerateUID();
  }

  if (criteria.relevantInstances.length == 0 && relevantInstance) {
    const copiedInstance = useDeepClone(relevantInstance);
    delete copiedInstance.criteria;
    criteria.relevantInstances = [copiedInstance];
  }
  saveEvaluationResults();

  if (updateMechanics) {
    await documentChecklistStore.onMechanicsCriteriaUpdated(criteria);
  }
};

const checkboxRef = ref();

const onClick = () => {
  const element = checkboxRef.value as HTMLElement | undefined;

  if (!element) {
    return;
  }

  element.click();
};

const onPropogateProblem = (
  instance: EvaluationRelevantInstance,
  isControl: boolean
) => {
  const criteriaCopy = { ...criteria };
  criteriaCopy.relevantInstances = [instance];

  emits("reportProblem", criteriaCopy, isControl);
};

const tabIndex = computed(() => {
  const checkboxes = document.querySelectorAll(".criteria-checkbox");
  const checkboxArray = [...checkboxes];

  var index = checkboxArray.indexOf(checkboxRef.value as HTMLElement) + 1;

  var base = paragraphPosition ?? 0;

  return (base + 1) * 10 + index;
});

const containerRef = ref();
// The need for this becomes obsolete once Mike adds an instance-uid to criteria
const criteriaIndex = computed(() => {
  const criterias = document.querySelectorAll(`.uid-${criteria.uid}`);
  const criteriaArray = [...criterias];

  const index = criteriaArray.indexOf(containerRef.value as HTMLElement) + 1;

  return index;
});

const appUserData = useAppUserDataStore();
const { isUserProductReviewer } = storeToRefs(appUserData);

const criteriaClickContainer = ref();

const documentViewMode = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewMode);

const showCriteria = computed(() => {
  if (isTeacherViewMode.value == true) return true;

  return criteria.config.shouldShowToStudents;
});

const showProblemController = useNewDialogController();
</script>

<template>
  <div
    :data-step-id="criteria.location"
    ref="containerRef"
    class="flex flex-col"
    :class="{
      'mb-2 border-b-2 pb-2': criteria.config.isVisible && isTopLevel,
      ['uid-' + criteria.uid]: true,
    }"
  >
    <div
      v-if="
        isTeacherViewMode != true &&
        criteria.config.missingInstancesMessage &&
        criteria.relevantInstances.length == 0
      "
      class="flex flex-row justify-between hover:bg-light-default px-2 min-h-[30px] mx-2 font-bold text-danger-default rounded"
    >
      {{ criteria.config.missingInstancesMessage }}
    </div>
    <div
      v-if="criteria.config.isMechanics == true"
      class="flex flex-row items-center justify-end"
    >
      <span class="text-[14px] pb-2 pr-4"> Mechanics Points </span>
    </div>
    <div
      :class="{
        'border-t-2 border-dashed pt-1': criteria.config.isMechanics == true,
      }"
    >
      <div
        v-if="criteria.config.isVisible && showCriteria"
        ref="criteriaClickContainer"
        @click="() => onClick()"
        :class="{
          ' mb-2': criteria.config.shouldShowInstances != true,
        }"
        class="cursor-pointer"
      >
        <span
          v-if="criteria.config.enableTypeSelection != true"
          class="flex flex-row justify-between hover:bg-light-default px-2 min-h-[30px] rounded"
          :class="{
            'py-[4px] leading-[30px]': isTopLevel,
          }"
        >
          <span class="flex flex-row items-center w-full">
            <span
              class="flex flex-row items-center"
              v-if="criteria.config.shouldShowInstances != true"
            >
              <span
                class="text-[12px] text-primary-default w-[30px] text-center"
              >
                <BaseIconWand
                  v-if="!criteria.config.isManual"
                  class="mt-[-2px] text-primary-default"
                />
              </span>

              <span
                v-if="criteria.config.isMechanics == true"
                class="text-[12px] text-primary-default mr-2 w-[30px] text-center"
              >
                <icon name="fluent:comma-20-filled" />
              </span>
              <input
                v-if="criteria.config.isMechanics != true"
                type="checkbox"
                v-model="criteria.passed"
                @update:model-value="onChange()"
                ref="checkboxRef"
                class="mr-2 w-[30px] mt-[2px] criteria-checkbox cursor-pointer pointer-events-none"
                :tabindex="tabIndex"
              />
            </span>
            <span
              class="select-none grow"
              :class="{
                'text-body-1 font-bold': isTopLevel,
                'text-body-2': !isTopLevel,
              }"
            >
              {{ criteria.config.text }}
            </span>
            <span
              v-if="criteria.config.isMechanics == true"
              class="flex flex-row items-center"
            >
              <BaseTextButton
                @click.stop="setPassedOverride(true)"
                color="success"
                class="h-[30px]"
                :class="{
                  'bg-success-default bg-opacity-10':
                    criteria.passed == true && criteria.isOverriden == true,
                }"
              >
                correct
              </BaseTextButton>
              <span class="mx-1">|</span>
              <BaseTextButton
                @click.stop="setPassedOverride(false)"
                color="danger"
                class="h-[30px]"
                :class="{
                  'bg-danger-default bg-opacity-10':
                    criteria.passed != true && criteria.isOverriden == true,
                }"
              >
                incorrect
              </BaseTextButton>
            </span>
          </span>
          <span v-if="isUserProductReviewer">
            <BasePopover :controller="showProblemController">
              <div
                class="h-[30px] w-[30px] ml-1 active:bg-opacity-20 flex flex-col items-center p-2 justify-center rounded hover:bg-medium-default hover:bg-opacity-10 transition-all duration-150 cursor-pointer"
                @click.stop.prevent="showProblemController.open()"
              >
                <icon name="material-symbols:flag" />
                <span class="sr-only">Flag {{ criteria.config.text || criteria.label || criteria.id }} suggestion</span>
              </div>
              <template #content>
                <EvaluationResultsFlagReviewForm
                  :criteria-instance="criteriaInstance"
                  target-kind="criterion"
                  :target-uid="criteria.uid"
                  :target-label="criteria.config.text || criteria.label || criteria.id"
                  :root-instance="rootReviewInstance"
                  :candidates="rootReviewInstance ? [] : criteria.relevantInstances"
                  @saved="showProblemController.close()"
                />
              </template>
            </BasePopover>
          </span>
          <span
            v-if="criteria.config.shouldShowInstances == true && isTeacherViewMode == true"
            class="text-success-default"
          >
            <FontAwesomeIcon icon="fa-solid fa-circle-check"></FontAwesomeIcon>
          </span>
        </span>
        <EvaluationResultsTypeSelection
          v-if="criteria.config.enableTypeSelection == true"
          :criteria-instance="criteriaInstance"
          :criteria="criteria"
          :relevant-instance="relevantInstance"
          :paragraph-position="paragraphPosition"
          :criteria-index="criteriaIndex"
          @on-toggle-passed="setPassedOverride($event, false)"
        />
      </div>
      <div v-if="criteria.config.shouldShowInstances">
        <DocumentPanelChecklistParagraphNoneFound
          v-if="
            criteria.relevantInstances.length == 0 && criteria.config.isVisible
          "
          :position="0"
          :checklist-criteria="checklistCriteria"
          class="ml-2"
        >
        </DocumentPanelChecklistParagraphNoneFound>
        <EvaluationResultsInstancesList
          :criteria-instance="criteriaInstance"
          :criteria-config="criteria.config"
          :relevant-instances="criteria.relevantInstances"
          :paragraph-position="paragraphPosition"
          @reportProblem="onPropogateProblem"
          :show-all="true"
        />
      </div>
      <div
        v-if="
          criteria.config.shouldShowInstances != true &&
          criteria.relevantInstances.length > 0
        "
      >
        <span v-for="(nestedInstance, index) in criteria.relevantInstances" :key="nestedInstance.uid || index">
          <EvaluationResultsCriteriaList
            :criteria-instance="criteriaInstance"
            :criteria-list="nestedInstance.criteria"
            :checklist-criteria="checklistCriteria"
            :relevant-instance="nestedInstance"
            :paragraph-position="paragraphPosition"
            @reportProblem="onPropogateProblem"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
